import { catchError, map, switchMap } from 'rxjs/operators';
import { from, Observable, of } from 'rxjs';

import { ActivatedRouteSnapshot, ResolveFn, Router } from '@angular/router';
import { Encryption } from '@app/modules/encryption/encryption.types';
import { EncryptionService } from '@app/modules/encryption/encryption.service';
import { inject } from '@angular/core';
import { License } from '@app/modules/license/license.types';
import { SessionService } from '@app/services/session.service';
import { SettingsService } from '@app/services/settings.service';
import { SpeakseeOrderService } from '@app/modules/license/license.service';
import { WebsocketService } from '@app/services/websocket.service';

export interface AvViewResolvedData {
  session: License;
  deviceId: string;
  jwtToken: string;
  display: string;
  shouldCleanUrl?: boolean;
}

/**
 * Resolver function for the AV view route.
 * Handles session resolution and WebSocket initialization.
 */
export const avViewResolver: ResolveFn<AvViewResolvedData> = (
  route: ActivatedRouteSnapshot
): Observable<AvViewResolvedData> => {
  // Inject dependencies
  const router = inject(Router);
  const sessionService = inject(SessionService);
  const websocketService = inject(WebsocketService);
  const orderService = inject(SpeakseeOrderService);
  const encryptionService = inject(EncryptionService);
  const settingsService = inject(SettingsService);

  const deviceId = route.params['id'];
  const display = route.queryParamMap.get('display') || 'HDMI-1';

  // Get JWT token from route data (set by AuthGuard)
  const avViewAuth = route.data['avViewAuth'];
  const jwtToken = avViewAuth?.jwtToken;
  const shouldCleanUrl = avViewAuth?.shouldCleanUrl;

  if (!jwtToken) {
    console.error('No JWT token available in route data');
    router.navigate(['/login']);
    throw new Error('Authentication required');
  }

  // First resolve the session (similar to SessionResolver logic)
  return orderService.getLicenseByIdOrSerial(deviceId).pipe(
    // Add encryption keys
    switchMap((license: License) => {
      return from(encryptionService.getOrCreateKeys(license.serial ?? license?.serials[0])).pipe(
        map((encryption: Encryption) => {
          license.encryption = encryption;
          return license;
        })
      );
    }),
    // Get remote settings for microphone kits
    switchMap((license: License) => {
      if (license.type === 'microphonekit') {
        return settingsService
          .getRemoteSetSettings(license.id, true)
          .pipe(map(() => license));
      }
      return of(license);
    }),
    // Start session
    switchMap((license: License) =>
      sessionService.startSession(license).pipe(map(() => license))
    ),
    // Return resolved data with JWT from auth guard
    switchMap((session: License) => {
      return of({
        session,
        deviceId,
        jwtToken,
        display,
        shouldCleanUrl
      });
    }),
    // Initialize WebSocket connection with resolved data
    map((data) => {
      if (data.jwtToken && data.deviceId && data.display) {
        websocketService.connect(data.deviceId, data.jwtToken, data.display);
        websocketService.start();
      } else {
        console.error('Missing required parameters for WebSocket connection');
        router.navigate(['/error'], {
          queryParams: { message: 'Invalid connection parameters' }
        });
      }
      return data;
    }),
    catchError((error) => {
      console.error('Failed to resolve AV view data:', error);
      router.navigate(['/microphone-kits']);
      throw error;
    })
  );
};